import '@material/web/icon/icon.js'
import '@operato/data-grist'
import '@operato/context/ox-context-page-toolbar.js'
import './data-sample-view.js'

import gql from 'graphql-tag'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { connect } from 'pwa-helpers/connect-mixin'
import moment from '@operato/moment-timezone-es'

import { DataGrist, FetchOption, GristRecord } from '@operato/data-grist'
import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { openPopup } from '@operato/layout'
import { PageView, store } from '@operato/shell'
import { CommonHeaderStyles, ScrollbarStyles } from '@operato/styles'
import { isMobileDevice } from '@operato/utils'
import { GhostPrint } from '@operato/ghost-print'

@customElement('data-sample-search-page')
export class DataSampleSearchPage extends connect(store)(localize(i18next)(PageView)) {
  static styles = [
    ScrollbarStyles,
    CommonHeaderStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;

        overflow: hidden;
      }

      ox-grist {
        overflow-y: auto;
        flex: 1;
      }

      .header {
        grid-template-areas: 'filters actions';
      }
    `
  ]

  @state() dataSetId?: string
  @state() dataSet: any
  @state() dataKeySet: any

  @state() gristConfig: any
  @state() mode: 'CARD' | 'GRID' | 'LIST' = isMobileDevice() ? 'CARD' : 'GRID'

  @query('ox-grist') private grist!: DataGrist

  get context() {
    return {
      title: i18next.t('title.data-sample search'),
      search: {
        handler: (search: string) => {
          this.grist.searchText = search
        },
        value: this.grist?.searchText || ''
      },
      filter: {
        handler: () => {
          this.grist.toggleHeadroom()
        }
      },
      help: 'dataset/data-sample-search',
      actions: [
        {
          title: i18next.t('button.print'),
          action: this.printSamples.bind(this),
          icon: 'print'
        }
      ],
      // exportable: {
      //   name: i18next.t('title.data-sample search'),
      //   data: this._exportableData.bind(this)
      // },
      toolbar: false
    }
  }

  render() {
    const mode = this.mode || (isMobileDevice() ? 'LIST' : 'GRID')

    return html`
      <ox-grist .mode=${mode} .config=${this.gristConfig} .fetchHandler=${this.fetchHandler.bind(this)}>
        <div slot="headroom" class="header">
          <div class="filters">
            <ox-filters-form class="filter" autofocus without-search></ox-filters-form>
          </div>

          <ox-context-page-toolbar class="actions" .context=${this.context}></ox-context-page-toolbar>
        </div>
      </ox-grist>
    `
  }

  pageUpdated(changes, lifecycle) {
    if (this.active) {
      this.dataSetId = lifecycle.resourceId
      return
    }
  }

  async updated(changes) {
    if (changes.has('dataSetId')) {
      const response = await client.query({
        query: gql`
          query ($id: String!) {
            dataSet(id: $id) {
              id
              name
              description
              dataKeySet {
                id
                name
                description
                dataKeyItems {
                  name
                  description
                  dataKey
                  tKey
                }
              }
            }
          }
        `,
        variables: {
          id: this.dataSetId
        }
      })

      this.dataSet = response.data.dataSet
      this.dataKeySet = this.dataSet.dataKeySet

      this.refreshGristConfig()
    }
  }

  getDataKeyColumns() {
    return (
      this.dataKeySet?.dataKeyItems.map((item, index) => {
        return {
          type: 'string',
          name: `key0${index + 1}`,
          header: i18next.t(item.tKey),
          record: {
            editable: false
          },
          sortable: true,
          filter: 'i_like',
          width: 120,
          imex: true
        }
      }) || []
    )
  }

  refreshGristConfig() {
    const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD')
    const today = moment().format('YYYY-MM-DD')

    this.gristConfig = {
      list: { fields: ['dataSet', 'data', 'updater', 'updatedAt'] },
      columns: [
        { type: 'gutter', gutterName: 'sequence', fixed: true },
        { type: 'gutter', gutterName: 'row-selector', fixed: true, multiple: true },
        {
          type: 'gutter',
          gutterName: 'button',
          fixed: true,
          icon: 'assignment',
          iconOnly: false,
          title: i18next.t('button.detail'),
          width: 72
        },
        {
          type: 'string',
          name: 'name',
          fixed: true,
          label: true,
          header: i18next.t('field.name'),
          record: {
            editable: false
          },
          filter: 'search',
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'description',
          label: true,
          header: i18next.t('field.description'),
          record: {
            editable: false
          },
          filter: 'search',
          width: 150,
          imex: true
        },
        ...this.getDataKeyColumns(),
        {
          type: 'checkbox',
          name: 'ooc',
          header: i18next.t('field.ooc'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'checkbox',
          name: 'oos',
          header: i18next.t('field.oos'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'json5',
          name: 'data',
          header: i18next.t('field.data'),
          record: {
            editable: false
          },
          hidden: true,
          imex: true
        },
        {
          type: 'string',
          name: 'workDate',
          header: i18next.t('field.work-date'),
          sortable: true,
          filter: {
            type: 'date',
            operator: 'between',
            value: [yesterday, today]
          },
          width: 80,
          imex: true
        },
        {
          type: 'string',
          name: 'workShift',
          header: i18next.t('field.work-shift'),
          sortable: true,
          width: 40,
          imex: true
        },
        {
          type: 'datetime',
          name: 'collectedAt',
          header: i18next.t('field.collected-at'),
          sortable: true,
          width: 180,
          imex: true
        },
        {
          type: 'resource-object',
          name: 'updater',
          header: i18next.t('field.updater'),
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'datetime',
          name: 'updatedAt',
          header: i18next.t('field.updated_at'),
          sortable: true,
          width: 180,
          imex: true
        }
      ],
      rows: {
        appendable: false,
        selectable: {
          multiple: true
        },
        classifier: function (record, rowIndex) {
          var emphasized
          if (record['oos']) {
            emphasized = ['#8B0000', 'white']
          } else if (record['ooc']) {
            emphasized = ['#FF6B6B', 'white']
          }

          return {
            emphasized
          }
        },
        handlers: {
          click: (columns, data, column, record, rowIndex) => {
            openPopup(
              html`
                <data-sample-view data-sample-id=${record.id} style="background-color: white;"></data-sample-view>
              `,
              {
                backdrop: true,
                size: 'large',
                title: i18next.t('title.data-sample view')
              }
            )
          }
        }
      },
      sorters: [
        {
          name: 'collectedAt',
          desc: true
        }
      ]
    }
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {
    if (!this.dataSetId) {
      return { records: [], total: 0 }
    }

    const response = await client.query({
      query: gql`
        query ($dataSetId: String!, $filters: [Filter!], $pagination: Pagination, $sortings: [Sorting!]) {
          responses: dataSamplesByDataSet(
            dataSetId: $dataSetId
            filters: $filters
            pagination: $pagination
            sortings: $sortings
          ) {
            items {
              id
              name
              description
              useCase
              key01
              key02
              key03
              key04
              key05
              data
              ooc
              oos
              judgment
              workDate
              workShift
              updater {
                id
                name
              }
              updatedAt
              collectedAt
            }
            total
          }
        }
      `,
      variables: {
        dataSetId: this.dataSetId,
        filters,
        pagination: { page, limit },
        sortings
      }
    })

    return {
      total: response.data.responses.total || 0,
      records: response.data.responses.items || []
    }
  }

  _exportableData() {
    let records = [] as GristRecord[]
    if (this.grist.selected && this.grist.selected.length > 0) {
      records = this.grist.selected
    } else {
      records = this.grist.data.records
    }

    var headerSetting = this.grist.compiledConfig.columns
      .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
      .map(column => {
        return column.imex === true
          ? {
              header: column.header.renderer(column),
              key: column.name,
              width: column.width,
              type: column.type
            }
          : column.imex
      })

    var data = records.map(item => {
      return {
        id: item.id,
        ...this.gristConfig.columns
          .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
          .reduce((record, column) => {
            const key = column.imex === true ? column.name : column.imex.key
            record[key] = key
              .split('.')
              .reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined), item)
            return record
          }, {})
      }
    })

    return { header: headerSetting, data: data }
  }

  printSamples() {
    const selected = this.grist.selected
    const ids = selected.map(record => record.id).join('|')

    GhostPrint.print({
      src: `./data-samples?ids=${ids}`
      // pending: 200 * selected.length
    })
  }
}
